import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@natu/utils/cn';

const badgeVariants = cva(
  'focus:ring-ring inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-medium font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'text-gray-700',
        primary: 'bg-primary text-primary-foreground hover:bg-primary/80 border-transparent',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 border-transparent',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/80 border-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof badgeVariants> {
  component?: React.ElementType;
}

const Badge = ({ className, variant, component: Component = 'div', ...props }: BadgeProps) => (
  <Component className={cn(badgeVariants({ variant }), className)} {...props} />
);

export interface BadgeListProps extends React.HTMLAttributes<HTMLUListElement> {
  items: { id: number | string; content: React.ReactNode }[];
  singleBadgeProps?: Omit<BadgeProps, 'as'>;
}

const BadgeList = ({ items, singleBadgeProps, className, ...props }: BadgeListProps) => (
  <ul className={cn('list-none space-x-2', className)} {...props}>
    {items.map(({ id, content }) => (
      <Badge component="li" key={id} {...singleBadgeProps}>
        {content}
      </Badge>
    ))}
  </ul>
);

export { Badge, BadgeList, badgeVariants };
